import { computeConcernRisk, severityToRisk, getAllConcernIds } from './combinedConcerns';

// Highest-risk-wins combined risk computation across red-flags + every concern's
// compact follow-up answers. Used by the new multi-concern Self-Check flow.
export const computeCombinedRisk = (redFlags, primaryConcern, relatedConcerns, concernFollowups) => {
  const order = { green: 0, yellow: 1, red: 2 };
  // Start with universal red-flag risk (no module hint).
  let best = computeRiskLevel(redFlags, null, {});
  const ids = getAllConcernIds({ primary_concern: primaryConcern, related_concerns: relatedConcerns });
  for (const cid of ids) {
    const sev = computeConcernRisk(cid, (concernFollowups || {})[cid] || {});
    const r = severityToRisk(sev);
    if (order[r] > order[best]) best = r;
  }
  return best;
};

// Risk level computation based on red-flag answers (and optional module hints)
export const computeRiskLevel = (redFlags, moduleId, moduleAnswers) => {
  const fever = redFlags?.fever;
  const chills = redFlags?.chills;
  const redness = redFlags?.redness;
  const pain = Number(redFlags?.pain_score ?? 0);
  const baby = redFlags?.baby_concern;

  // RED conditions
  if (fever === 'high') return 'red';
  if (chills === 'obvious' && (redness === 'obvious' || redness === 'spreading' || redness === 'mild')) return 'red';
  if (redness === 'spreading') return 'red';
  if (baby === 'sleepy' || baby === 'fewer_diapers') return 'red';
  if (pain >= 8 && (fever === 'high' || fever === 'mild' || redness === 'obvious' || redness === 'mild' || redness === 'spreading')) return 'red';

  // Module C always at least Yellow, often Red — handled by force_red flag separately
  if (moduleId === 'C') {
    const temp = moduleAnswers?.temp;
    const dur = moduleAnswers?.fever_duration;
    const area = moduleAnswers?.red_area;
    if (temp === '38.0-38.4' || temp === '38.5+') return 'red';
    if (dur === 'gt24h' || dur === 'recurrent') return 'red';
    if (area === 'spreading') return 'red';
    return 'yellow';
  }

  // YELLOW conditions
  if (fever === 'mild') return 'yellow';
  if (pain >= 5 && pain <= 7) return 'yellow';
  if (redness === 'mild' || redness === 'obvious') return 'yellow';

  if (moduleId === 'B') {
    const dur = moduleAnswers?.lump_duration;
    const drop = moduleAnswers?.supply_drop;
    if (dur === '1-2d' || dur === 'gt2d' || dur === 'recurrent') return 'yellow';
    if (drop === 'a_lot') return 'yellow';
  }
  if (moduleId === 'D') {
    const app = moduleAnswers?.nipple_appearance;
    if (app === 'cracked' || app === 'bleeding') return 'yellow';
  }
  if (moduleId === 'E') {
    const lump = moduleAnswers?.lump_behind;
    if (lump === 'lump' || lump === 'lump_low') return 'yellow';
  }
  if (moduleId === 'F') {
    const sym = moduleAnswers?.pump_symptom;
    if (sym === 'pain' || sym === 'white' || sym === 'swollen') return 'yellow';
  }

  // Module H — latch difficulty. Per spec: do NOT auto-escalate. We only return
  // red if a real red flag fired above (fever HIGH, redness spreading, baby
  // sleepy/fewer-diapers). Otherwise default to yellow when there's mild pain
  // or a meaningful latch issue, else green.
  if (moduleId === 'H') {
    if (pain >= 5) return 'yellow';
    const main = moduleAnswers?.latch_main;
    const softens = moduleAnswers?.softens_after;
    if (main === 'painful' || main === 'shape_change' || softens === 'no') return 'yellow';
    return 'green';
  }

  // Module I — back-to-work pumping. Real red flags from above still apply.
  if (moduleId === 'I') {
    const sessions = moduleAnswers?.work_sessions;
    const freq = moduleAnswers?.pump_freq_now;
    const ret = moduleAnswers?.return_when;
    if (sessions === 'difficult' || freq === 'rarely') return 'yellow';
    if (ret === 'lt1w' && freq === 'irregular') return 'yellow';
    return 'green';
  }

  // Module J — weaning / reducing supply.
  if (moduleId === 'J') {
    const recent = moduleAnswers?.recent_issues;
    const current = moduleAnswers?.current_pain;
    const pace = moduleAnswers?.wean_pace;
    if (recent === 'mastitis' || current === 'red') return 'red';
    if (recent === 'recurring' || recent === 'clog' || current === 'lump' || current === 'severe') return 'yellow';
    if (pace === 'asap') return 'yellow';
    return 'green';
  }

  return 'green';
};
